import FilterListIcon from "@mui/icons-material/FilterList";
import { Chip, ChipProps, Stack, Tooltip, Typography } from "@mui/material";
import {
  FilterPanelTrigger,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  Toolbar,
  ToolbarButton,
} from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import {
  formatReviewReason,
  getHighestPriorityReasonConfig,
} from "components/manualReview/utils/reviewReasons";
import { getLabellingMaterialDisplayName } from "components/scrapEyeLabelling/utils/materialDisplayName";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { useMaterials } from "hooks/useMaterials";
import { useCallback, useMemo } from "react";
import {
  GroupStatus,
  groupStatus,
  mergeFlagBreakdowns,
  TargetedReviewPeriodGroup,
  unexplainedFlaggedCount,
} from "./targetedReviewRows";

dayjs.extend(utc);

const VERIFIED_TIME_FORMAT = "YYYY-MM-DD HH:mm";
const GROUP_TIME_FORMAT = "HH:mm";

const getAutoRowHeight = () => "auto" as const;

const DISPOSITION_CHIP_PROPS: Record<
  GroupStatus,
  Pick<ChipProps, "variant" | "color">
> = {
  verifiedLinked: { variant: "outlined" },
  reviewed: { color: "success" },
  skipped: {},
  notReviewed: {},
};

const groupFlaggedCount = (group: TargetedReviewPeriodGroup): number =>
  group.rows.reduce((sum, row) => sum + row.flaggedCount, 0);

const latestVerifiedRow = (
  group: TargetedReviewPeriodGroup
): TargetedReviewPeriodGroup["rows"][number] | undefined =>
  group.rows.reduce<TargetedReviewPeriodGroup["rows"][number] | undefined>(
    (latest, row) => {
      if (!row.reviewPeriod?.verified_at) return latest;
      if (!latest?.reviewPeriod?.verified_at) return row;
      return dayjs
        .utc(row.reviewPeriod.verified_at)
        .isAfter(dayjs.utc(latest.reviewPeriod.verified_at))
        ? row
        : latest;
    },
    undefined
  );

const TargetedReviewToolbar = () => {
  const { t } = useTenantTranslation();
  return (
    <Toolbar>
      <Tooltip title={t("filter")}>
        <FilterPanelTrigger render={<ToolbarButton />}>
          <FilterListIcon fontSize="small" />
        </FilterPanelTrigger>
      </Tooltip>
    </Toolbar>
  );
};

export const TargetedReviewTable = ({
  groups,
  reviewedIds,
  broughtInIds,
  seenIds,
  onGroupClick,
}: {
  groups: TargetedReviewPeriodGroup[];
  reviewedIds: Set<string>;
  // Checked ahead of reviewedIds so it reads distinctly, not as "Reviewed".
  broughtInIds: Set<string>;
  seenIds: Set<string>;
  onGroupClick: (group: TargetedReviewPeriodGroup) => void;
}) => {
  const { t } = useTenantTranslation();
  const { materials: materialMetadata } = useMaterials();
  const labellingNameByMaterial = useMemo(() => {
    const byName = new Map<string, string>();
    (materialMetadata ?? []).forEach((material) => {
      byName.set(material.name, getLabellingMaterialDisplayName(material, t));
    });
    return byName;
  }, [materialMetadata, t]);

  const columns = useMemo<GridColDef<TargetedReviewPeriodGroup>[]>(
    () => [
      {
        field: "time",
        headerName: t("time"),
        width: 150,
        display: "flex",
        valueGetter: (_value, group) =>
          `${dayjs.utc(group.start).format(GROUP_TIME_FORMAT)}–${dayjs
            .utc(group.end)
            .format(GROUP_TIME_FORMAT)} UTC`,
      },
      {
        field: "flaggedCount",
        headerName: t("flagged"),
        flex: 3,
        minWidth: 280,
        display: "flex",
        valueGetter: (_value, group) => groupFlaggedCount(group),
        renderCell: (params) => {
          const unexplainedCount = unexplainedFlaggedCount(params.row.rows);
          const flagBreakdown = mergeFlagBreakdowns(
            params.row.rows.map((row) => row.flagBreakdown)
          );
          return (
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              sx={{ columnGap: 1, rowGap: 0.5, py: 0.5 }}
            >
              {params.row.rows.some((row) => row.selectedRandomly) ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t("randomCheck")}
                  sx={{ borderColor: "grey.600", color: "grey.600" }}
                />
              ) : null}
              {flagBreakdown.map(({ reason, eventCount, materials }) => {
                const themeColor = getHighestPriorityReasonConfig([
                  reason,
                ])?.themeColor;
                const label =
                  eventCount > 1
                    ? `${formatReviewReason(reason)} · ${eventCount}`
                    : formatReviewReason(reason);
                return (
                  <Tooltip
                    key={reason}
                    title={
                      materials.length > 0
                        ? materials
                            .map(
                              (material) =>
                                labellingNameByMaterial.get(material) ??
                                material
                            )
                            .join(", ")
                        : formatReviewReason(reason)
                    }
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      label={label}
                      sx={
                        themeColor
                          ? { borderColor: themeColor, color: themeColor }
                          : undefined
                      }
                    />
                  </Tooltip>
                );
              })}
              {unexplainedCount > 0 ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${t("flagged")} · ${unexplainedCount}`}
                />
              ) : null}
            </Stack>
          );
        },
      },
      {
        field: "disposition",
        headerName: t("disposition"),
        flex: 1,
        minWidth: 140,
        display: "flex",
        valueGetter: (_value, group) =>
          t(groupStatus({ group, reviewedIds, broughtInIds, seenIds })),
        renderCell: (params) => {
          const status = groupStatus({
            group: params.row,
            reviewedIds,
            broughtInIds,
            seenIds,
          });
          return (
            <Chip
              size="small"
              {...DISPOSITION_CHIP_PROPS[status]}
              label={t(status)}
            />
          );
        },
      },
      {
        field: "verified",
        headerName: t("verified"),
        flex: 2,
        minWidth: 200,
        display: "flex",
        valueGetter: (_value, group) => {
          const period = latestVerifiedRow(group)?.reviewPeriod;
          if (!period?.verified_at) return "";
          const at = `${dayjs
            .utc(period.verified_at)
            .format(VERIFIED_TIME_FORMAT)} UTC`;
          return period.verified_by_name
            ? `${period.verified_by_name} · ${at}`
            : at;
        },
        renderCell: (
          params: GridRenderCellParams<TargetedReviewPeriodGroup, string>
        ) => (
          <Typography variant="body2" noWrap title={params.value}>
            {params.value}
          </Typography>
        ),
      },
    ],
    [t, reviewedIds, broughtInIds, seenIds, labellingNameByMaterial]
  );

  const getRowId = useCallback(
    (group: TargetedReviewPeriodGroup) => group.id,
    []
  );

  return (
    <HotkeyCompliantDataGrid<TargetedReviewPeriodGroup>
      rows={groups}
      columns={columns}
      getRowId={getRowId}
      getRowHeight={getAutoRowHeight}
      onRowClick={(params: GridRowParams<TargetedReviewPeriodGroup>) => {
        onGroupClick(params.row);
      }}
      onCellKeyDown={(
        params: GridCellParams<TargetedReviewPeriodGroup>,
        event
      ) => {
        if (event.key === "Enter") {
          onGroupClick(params.row);
        }
      }}
      showToolbar
      slots={{ toolbar: TargetedReviewToolbar }}
      disableColumnSelector
      disableRowSelectionOnClick
      sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
    />
  );
};
